import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  HELPER_TEXT_TYPES,
  helpersIsEmpty,
  selectContext,
  selectFieldValue,
} from '@icp/form-renderer-core';
import { restApi } from '@icp/settings';
import { useElementDecorator, useIsInDesign } from '../../FormRenderCtx';
import { useClassName } from '../../hooks';
import { withFieldWrapper } from '../../fieldWrapper';
import { ConditionalPropertyPropType } from '../../propTypes';
import Workflow from './Workflow';
import FieldTitle from '../../FieldTitle';
import { useSelector } from '../../store';

const WorkflowElement = forwardRef(function WorkflowElement(props, ref) {
  const {
    keyPath,
    id,
    className: classNameProp,
    title,
    value,
    disabled,
    readonly,
    style,
    validation,
    // status,
    helpers,
    onChange,
    // onTouchChanged,
    fieldTitleProps,
    componentProps = {},
  } = props;

  // const { ...otherComponentProps } = componentProps;

  const ElementDecorator = useElementDecorator();
  const isInDesign = useIsInDesign();
  const context = useSelector(selectContext);
  const entityValue = useSelector(selectFieldValue('entity'));

  const className = useClassName(classNameProp);
  const classNameComp = useClassName(componentProps.className);

  const [workflowTypeNodeList, setWorkflowTypeNodeList] = useState([]);
  const [entity, setEntity] = useState(null);

  const nodeRef = useRef(null);

  useImperativeHandle(
    ref,
    () => ({
      node: nodeRef.current,
    }),
    [],
  );

  useEffect(() => {
    if (!entityValue) {
      return;
    }
    const [pbcToken, formEntityToken] = entityValue[0].value.split('/');
    restApi
      .getStatic(`/form/api/v2/form-entity/${pbcToken}/${formEntityToken}`, {
        params: { needLayouts: false },
      })
      .then((formEntity) => {
        return restApi
          .get(
            `/flow/api/workflow-type/workflow-type-node/list-by-token/${pbcToken}/${formEntity.workflowTypeToken}`,
          )
          .then((res) => {
            return [formEntity, res];
          });
      })
      .then((res) => {
        setEntity(res[0]);
        setWorkflowTypeNodeList(res[1]);
      });
  }, [context.pbcToken, entityValue]);

  const handleChange = (newDefinition) => {
    onChange(JSON.stringify(newDefinition));
  };

  return (
    <ElementDecorator keyPath={keyPath} id={id}>
      <div
        className={clsx(
          'workflow-element',
          'input-element',
          'form-element',
          {
            'has-helper': !helpersIsEmpty(helpers),
          },
          className,
        )}
        style={style}
        ref={ref}
      >
        <FieldTitle required={validation?.required} {...fieldTitleProps}>
          {title}
        </FieldTitle>
        <div>
          <Workflow
            {...componentProps}
            id={id}
            className={classNameComp}
            readonly={isInDesign || readonly || disabled}
            workflowTypeNodeList={workflowTypeNodeList}
            entity={entity}
            value={value ? JSON.parse(value) : null}
            onChange={handleChange}
          />
        </div>
      </div>
    </ElementDecorator>
  );
});

WorkflowElement.propTypes = {
  keyPath: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  id: PropTypes.string,
  className: PropTypes.string,
  title: PropTypes.string,
  value: PropTypes.string,
  componentProps: PropTypes.shape({
    /**
     * 组件的 className
     */
    className: PropTypes.string,
    /**
     * 组件的 style
     */
    style: PropTypes.shape({}),
  }),
  fieldTitleProps: PropTypes.shape({
    showColon: PropTypes.bool,
  }),
  validation: PropTypes.shape({
    required: PropTypes.bool,
  }),
  disabled: ConditionalPropertyPropType(PropTypes.bool),
  readonly: ConditionalPropertyPropType(PropTypes.bool),
  status: PropTypes.oneOf(HELPER_TEXT_TYPES),
  helpers: PropTypes.arrayOf(
    PropTypes.shape({
      status: PropTypes.oneOf(HELPER_TEXT_TYPES),
      text: PropTypes.string,
    }),
  ),
  onChange: PropTypes.func,
  onTouchChanged: PropTypes.func,
};

// for @icp/utils/getComponentDisplayName, otherwise, in production mode, function name will be compressed.
WorkflowElement.displayName = 'Workflow';

export default withFieldWrapper(WorkflowElement);
